import GlobalContext from 'Contexts/GlobalContext'
import { ReactElement, useContext, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { clamp, formatNumber, roomSizeStringFormatter } from 'Utils'
import { ArrowIcon } from './ArrowIcon'
import customMunicipalities from 'components/ChoroplethMap/Assets/municipalities.json'
import BarStacked from 'components/BarStacked'
import CloseIcon from './CloseIcon'
import { de } from 'Globals/Locale'
import { COLOR_PALLETTE, ROOM_PRICE_NAMES } from 'Globals/Constants'
const COLORS = {
  neutral: 'gray',
  flatsInCorrectSize: COLOR_PALLETTE[0],
  flatsInBudget: COLOR_PALLETTE[2],
}
interface Municipality {
  ORTNAME: string
  PLZ4: number
  PLZ6: number
  GDENAMK: string
  GDENR: number
  KTKZ: string
  NORMORTSNAME: string
  NORMGEMEINDE: string
}
const ToolTip: React.FC<{
  activeData: MietpreisGemeinde
  currentRoomSize?: keyof MietpreisGemeinde
  currentPrice?: number
  isClosable?: boolean
  hasShadow?: boolean
  closePressed?: () => void
}> = ({
  activeData,
  currentRoomSize = 'Zi_3–3.5',
  currentPrice = 2,
  isClosable,
  hasShadow,
  closePressed,
}): ReactElement => {
  const { locale } = useContext(GlobalContext) ?? {locale: de}
  const prices = activeData?.[currentRoomSize] as number[]
  const numberOfFlatsInBudget = prices?.[currentPrice]
  const activeMunicipality = customMunicipalities.find(
    (muni: Municipality) => muni?.GDENR === activeData?.bfsId
  )
  const toolTipRef = useRef<HTMLDivElement>(null)
  const currentFlatSize = roomSizeStringFormatter(currentRoomSize)
  return (
    <ToolTipWrapper ref={toolTipRef} hasShadow={hasShadow}>
      {activeData && locale && (
        <>
          {
            <Head>
              <h4>
                {activeMunicipality?.NORMGEMEINDE === 'Zürich'
                  ? activeMunicipality.ORTNAME
                  : activeMunicipality?.NORMGEMEINDE}
                {/* {activeData["name_"+(mfProps?.tenant?.lang ?? "de") as "name_de" | "name_fr"]} */}
              </h4>
              {isClosable && (
                <ClosingButton
                  className='close-button'
                  onClick={(e) => {
                    e.stopPropagation()
                    closePressed?.()
                  }}
                >
                  <CloseIcon />
                </ClosingButton>
              )}
            </Head>
          }
          <Body>
            {/* <Row>
              <EntryValue color={COLORS.neutral}>
                {activeData.Tot}&nbsp;
              </EntryValue>
              <EntryTitle className='provider'> {locale.offers}</EntryTitle>
            </Row> */}
            <Row>
              <EntryValue color={'var(--text-color)'}>
                {formatNumber(prices[prices.length-1] ?? 0)}&nbsp;
              </EntryValue>
              <EntryTitle className='provider'>
                {locale.areRoomApartments.replace('FLATSIZE', currentFlatSize)}
              </EntryTitle>
            </Row>
            <Row>
              <EntryValue color={COLORS.flatsInBudget}>
                {formatNumber(numberOfFlatsInBudget)}&nbsp;
              </EntryValue>
              <EntryTitle className='provider'>{locale.areInBudgetOf} {ROOM_PRICE_NAMES[currentPrice]} CHF</EntryTitle>
            </Row>
            <Row>
              <EntryValue color={COLORS.flatsInCorrectSize}>
                {(prices[prices.length-1] ?? 0) - numberOfFlatsInBudget }&nbsp;
              </EntryValue>
              <EntryTitle className='provider'>
              {locale.areNotInBudget}
              </EntryTitle>
            </Row>
            {/* <BarStacked
              backgroundColor={COLORS.flatsInCorrectSize}
              values={[
                {
                  color: COLORS.flatsInBudget,
                  value: (numberOfFlatsInBudget / (prices[prices.length-1] ?? 1)) * 100,
                },
              ]}
            /> */}
          </Body>
        </>
      )}
    </ToolTipWrapper>
  )
}
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1em;
  margin-bottom: 0;
  h4{
  font-size: 1.5em;
  margin: 0;
}
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1em;
  margin-top: 0;

`
const Row = styled.div`
  display: flex;
`
const EntryTitle = styled.div``
const EntryValue = styled.b<{ color: string }>`
  color: ${(props) => props.color};
  min-width: 30px;
  font-size: 1.2em;
`
const ClosingButton = styled.button`
  pointer-events: all;
  font-size: 1.5rem;
  line-height: 1;
  padding: 0.5rem;
  border: none;
  background-color: transparent;
  // disable flashing when pressing button
  -webkit-tap-highlight-color: transparent;
  color: inherit;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    background-color: var(--color-black);
    color: var(--color-white);
  }
`
const RelChange = styled.div<{ change: number }>`
  display: flex;
  /* justify-content: flex-end; */
  align-items: center;
  flex-direction: row;
  color: ${(props) =>
    props.change > 0
      ? 'var(--change-color-red)'
      : props.change < 0
      ? 'var(--change-color-green)'
      : 'currentColor'};
  .arrow-icon {
    transition: all 500ms cubic-bezier(0.77, 0, 0.175, 1);
    width: 1em;
    height: 1em;
    margin-right: 0.5em;
    margin-left: 0.5em;
  }
`
const ToolTipWrapper = styled.div<{ hasShadow?: boolean }>`
  pointer-events: none;
  background-color: var(--tooltip-background-color);
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
   font-size: 14px;
  box-shadow: rgba(0, 0, 0, 0.03) 0px 4.5px 1.3px, rgba(0, 0, 0, 0.035) 0px 8.6px 3.1px, rgba(0, 0, 0, 0.035) 0px 12.7px 5.5px, rgba(0, 0, 0, 0.04) 0px 17.3px 9.2px, rgba(0, 0, 0, 0.04) 0px 23.4px 15.1px, rgba(0, 0, 0, 0.047) 0px 33.5px 26.4px, rgba(0, 0, 0, 0.07) 0px 60px 57px;
  font-family: var(--font-sans-serif);
  min-width: 250px;
  max-width: 400px;
  animation: slide-in-top 0.3s ease-in-out;
  @keyframes slide-in-top {
    0% {
      transform: translate3d(0, -50px, 0);
      opacity: 0;
      max-height: 0;
    }
    100% {
      transform: translate3d(0, 0, 0);
      opacity: 1;
      max-height: 400px;
    }
  }
  
`
export default ToolTip
